import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Note, User } from '../../types/api';
import apiService from '../../services/api';
import { formatRelativeDateTime } from '../../utils/dateUtils';

interface NotesListProps {
    objectType: string; // 'task', 'patient', etc.
    objectId: number;
    users: User[];
    currentUserId?: number; // Optional current user ID to enable edit functionality
}

export interface NotesListRef {
    submitPendingNote: () => Promise<void>;
}

export const NotesList = forwardRef<NotesListRef, NotesListProps>(({ objectType, objectId, users, currentUserId }, ref) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newNoteText, setNewNoteText] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [hoveredNoteId, setHoveredNoteId] = useState<number | null>(null);

    const token = localStorage.getItem('token') || '';
    const loggedInUserId = currentUserId || parseInt(localStorage.getItem('user_id') || '0', 10);
    const isFetchingRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
        submitPendingNote: async () => {
            if (newNoteText.trim()) {
                await handleAddNote();
            }
        },
    }));

    const userNameMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [users]);

    const getUserName = React.useCallback((userId: number): string => {
        return userNameMap[userId] || 'Unknown User';
    }, [userNameMap]);

    const isHebrew = (text: string) => /[\u0590-\u05FF]/.test(text);

    const scrollToBottom = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    };

    const fetchNotes = React.useCallback(async () => {
        if (!token || !objectId) return;
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setIsLoading(true);
        setError(null);
        try {
            const notesData = await apiService.getNotes({
                token,
                object: objectType,
                object_id: objectId,
            });
            // Sort oldest → newest
            const sorted = [...notesData].sort((a, b) => a.created_at - b.created_at);
            setNotes(sorted);
        } catch (err: any) {
            console.error('Failed to load notes:', err);
            setError('Failed to load notes');
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    }, [token, objectType, objectId]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    // Scroll to bottom after load and whenever notes update
    useEffect(() => {
        if (!isLoading) {
            scrollToBottom();
        }
    }, [notes, isLoading]);

    const handleAddNote = async () => {
        if (!newNoteText.trim()) return;
        setIsSaving(true);
        try {
            await apiService.createNote({
                token,
                object: objectType,
                object_id: objectId,
                text: newNoteText.trim(),
            });
            setNewNoteText('');
            await fetchNotes();
        } catch (err: any) {
            console.error('Failed to create note:', err);
            setError('Failed to create note');
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddNote();
        }
    };

    const handleEditNote = (note: Note) => {
        setEditingNoteId(note.id);
        setEditingText(note.text);
    };

    const handleSaveEdit = async (noteId: number) => {
        if (!editingText.trim()) return;
        setIsSaving(true);
        try {
            await apiService.updateNote({
                token,
                id: noteId,
                object: objectType,
                object_id: objectId,
                text: editingText.trim(),
            });
            setEditingNoteId(null);
            setEditingText('');
            await fetchNotes();
        } catch (err: any) {
            console.error('Failed to update note:', err);
            setError('Failed to update note');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingNoteId(null);
        setEditingText('');
    };

    return (
        <div className="flex flex-col gap-2">
            {/* Chat Bubbles Container */}
            <div
                ref={scrollContainerRef}
                className="flex flex-col gap-3 rounded-lg p-3 overflow-y-auto"
                style={{ minHeight: '140px', maxHeight: '280px', backgroundColor: '#C8E6E3' }}
            >
                {isLoading && (
                    <div className="text-sm text-gray-600 italic text-center py-2">Loading notes...</div>
                )}
                {error && (
                    <div className="text-sm text-red-600 text-center">{error}</div>
                )}
                {!isLoading && !error && notes.length === 0 && (
                    <div className="text-sm text-gray-500 italic text-center py-4">No notes yet</div>
                )}

                {notes.map((note) => {
                    const isOwn = loggedInUserId > 0 && note.created_by === loggedInUserId;
                    const isRTL = isHebrew(note.text);
                    const isEditing = editingNoteId === note.id;
                    const isHovered = hoveredNoteId === note.id;

                    return (
                        <div
                            key={note.id}
                            className="w-full flex flex-col"
                            onMouseEnter={() => setHoveredNoteId(note.id)}
                            onMouseLeave={() => setHoveredNoteId(null)}
                        >
                            {/* Name + timestamp */}
                            <div className={`flex items-center gap-1 mb-1 text-xs text-gray-600 ${isOwn ? 'flex-row-reverse' : ''}`}>
                                <span className="font-semibold">{getUserName(note.created_by)}</span>
                                <span className="font-normal">{formatRelativeDateTime(note.created_at)}</span>
                            </div>

                            {/* Bubble */}
                            {isEditing ? (
                                <div className="flex gap-2 w-full max-w-[90%]">
                                    <textarea
                                        value={editingText}
                                        onChange={(e) => setEditingText(e.target.value)}
                                        className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                        style={{ minHeight: '60px', direction: isRTL ? 'rtl' : 'ltr' }}
                                        autoFocus
                                        disabled={isSaving}
                                    />
                                    <div className="flex flex-col gap-1">
                                        <button
                                            onClick={() => handleSaveEdit(note.id)}
                                            disabled={isSaving || !editingText.trim()}
                                            className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Save"
                                        >✓</button>
                                        <button
                                            onClick={handleCancelEdit}
                                            disabled={isSaving}
                                            className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50"
                                            title="Cancel"
                                        >✕</button>
                                    </div>
                                </div>
                            ) : (
                                <div className={`flex items-center gap-1 w-full ${isOwn ? 'justify-end' : 'justify-start'}`}>
                                    {/* Edit button — always rendered for own notes to avoid layout shift/flickering */}
                                    {isOwn && (
                                        <button
                                            onClick={() => handleEditNote(note)}
                                            className={`text-gray-500 hover:text-blue-600 text-sm p-0.5 flex-shrink-0 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                                            title="Edit note"
                                        >✎</button>
                                    )}
                                    <div
                                        className={`max-w-[90%] px-3 py-2 text-sm leading-normal shadow-sm ${
                                            isOwn
                                                ? 'bg-white text-gray-800 rounded-2xl rounded-tr-sm'
                                                : 'bg-[#D9D9D9] text-gray-800 rounded-2xl rounded-tl-sm'
                                        }`}
                                        style={isRTL ? { direction: 'rtl' } : undefined}
                                    >
                                        {note.text}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Input Area — always visible */}
            {error && (
                <div className="text-xs text-red-600">{error}</div>
            )}
            <div className="flex gap-2 items-end">
                <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Write a note… (Enter to send, Shift+Enter for new line)"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#366A75] resize-none"
                    style={{ minHeight: '40px', maxHeight: '100px' }}
                    disabled={isSaving}
                    rows={1}
                />
                <button
                    onClick={handleAddNote}
                    disabled={isSaving || !newNoteText.trim()}
                    className="px-4 py-2 text-sm bg-[#366A75] text-white rounded-lg hover:bg-[#2d5a63] disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                    {isSaving ? '…' : 'Send'}
                </button>
            </div>
        </div>
    );
});
